import { useId, useMemo, useState } from 'react'
import {
  PASSWORT_HINWEIS,
  PASSWORT_MIN,
  erzeugeStarkesPasswort,
  pruefePasswort,
  type Passwortumfeld
} from '@raporto/shared'

/**
 * Das Feld, in dem ein neues Passwort entsteht.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Warum ein eigenes Bauteil
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Es gibt drei Stellen, an denen jemand ein Passwort SETZT: bei der
 * Bestellung, beim Einlösen eines Links (Rücksetzung und Einladung) und in
 * den Sicherheitseinstellungen. Dreimal dieselbe Hinweiszeile, dreimal
 * derselbe Knopf, dreimal dieselbe Prüfung – und beim nächsten Umbau
 * zweieinhalb Mal.
 *
 * Felder, in denen jemand sein BESTEHENDES Passwort eingibt (Anmeldung,
 * Sperrbildschirm, Nachweis vor einer heiklen Handlung), benutzen dieses
 * Bauteil ausdrücklich NICHT. Dort wäre ein Vorschlagsknopf sinnlos und die
 * Regelanzeige verwirrend: Wer sich anmeldet, soll nicht lesen, dass sein
 * Passwort zu kurz sei.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Der Server entscheidet
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Was hier angezeigt wird, kommt aus `pruefePasswort()` in
 * `packages/shared` – derselben Funktion, die der Server aufruft. Das ist
 * Bequemlichkeit und keine Prüfung: Dieses Bauteil kann niemanden aufhalten.
 * Wer die Oberfläche umgeht, bekommt dieselbe Absage vom Server, nur
 * später.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Was mit dem vorgeschlagenen Passwort NICHT passiert
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Es wird nicht gespeichert. Nicht in `localStorage`, nicht in
 * `sessionStorage`, nicht in der Adresszeile, nicht in einem Protokoll und
 * nicht vorab an den Server. Es steht im Zustand dieses Bauteils und
 * verschwindet mit ihm. Wer es nicht kopiert hat, bevor er die Seite
 * verlässt, hat es verloren – und das ist richtig so: Ein Vorschlag, den
 * man später noch einmal abrufen kann, ist irgendwo abgelegt.
 */

/**
 * Raporto hat zwei Oberflächenwelten mit eigenen Klassen: die öffentliche
 * Seite (`rp-…`, `public.css`) und den Bürobereich (`o-…`, `office.css`).
 * Dieses Bauteil erfindet dafür keine dritte, sondern nimmt die Klassen der
 * Welt, in der es steht. Neue Farbtokens gibt es keine.
 */
export type Stil = 'oeffentlich' | 'buero' | 'bestellung' | 'anmeldung'

const KLASSEN = {
  oeffentlich: {
    feld: 'rp-feld',
    hinweis: 'rp-hinweis',
    fehler: 'rp-fehler',
    knopf: 'rp-btn rp-btn-still',
    zeile: ''
  },
  /*
    Die Anmeldekarte – dieselbe, auf der man sich anmeldet und über einen
    Link sein Passwort setzt. Keine neuen Klassen: Das sind genau die, mit
    denen `Login.tsx` seine Felder baut.

    `zeile` ist dabei nicht nebensächlich. Ohne sie trägt das Eingabefeld die
    Gestaltung aus `styles.css` – hell, für den Bürobereich gedacht – und
    steht als weisser Block auf einer dunklen Karte. Gemessen hat das der
    Browsertest, nicht das Auge.
  */
  anmeldung: {
    feld: 'rp-an-feld',
    hinweis: 'rp-an-hinweis',
    fehler: 'rp-an-fehler',
    knopf: 'rp-pg-knopf rp-pg-knopf-glas rp-pg-knopf-klein',
    zeile: 'rp-an-eingabe'
  },
  buero: {
    feld: 'o-field',
    hinweis: 'hint',
    fehler: 'hint o-pw-fehler',
    knopf: 'o-btn o-btn-ghost',
    zeile: ''
  },
  bestellung: {
    feld: 'rp-best-feld',
    hinweis: 'rp-best-hinweis',
    fehler: 'rp-best-fehler',
    knopf: 'rp-btn rp-btn-still',
    zeile: ''
  }
} as const

export interface PasswortfeldProps {
  id?: string
  stil?: Stil
  label?: string
  wert: string
  aufWert: (wert: string) => void
  /** Was nicht im Passwort stehen darf – Mailadresse, Name, Betrieb. */
  umfeld?: Passwortumfeld
  /** Soll der Vorschlagsknopf erscheinen? Vorgabe: ja. */
  mitVorschlag?: boolean
  autoFocus?: boolean
  disabled?: boolean
}

/**
 * Der Zufall kommt aus der Web Crypto API.
 *
 * `Math.random()` wäre hier ein Fehler mit Ansage: Der Generator dahinter
 * ist auf Geschwindigkeit gebaut und nicht auf Unvorhersagbarkeit – aus
 * wenigen beobachteten Werten lässt sich der weitere Verlauf berechnen. Für
 * ein Passwort, das einen Betrieb schützt, ist das untauglich.
 */
function zufall(anzahl: number): Uint8Array {
  const puffer = new Uint8Array(anzahl)
  crypto.getRandomValues(puffer)
  return puffer
}

export function Passwortfeld({
  id,
  stil = 'oeffentlich',
  label = 'Neues Passwort',
  wert,
  aufWert,
  umfeld,
  mitVorschlag = true,
  autoFocus,
  disabled
}: PasswortfeldProps): JSX.Element {
  const k = KLASSEN[stil]
  const eigeneId = useId()
  const feldId = id ?? `pw-${eigeneId}`
  const [sichtbar, setSichtbar] = useState(false)
  const [kopiert, setKopiert] = useState(false)

  /*
    Geprüft wird erst ab einer gewissen Länge.

    Wer gerade das dritte Zeichen tippt, soll nicht lesen, dass sein
    Passwort zu kurz sei – das weiss er. Die Meldung kommt, wenn sie
    hilfreich wird: entweder weil die Mindestlänge erreicht ist und etwas
    anderes nicht stimmt, oder weil das Feld verlassen wurde.
  */
  const befund = useMemo(() => {
    if (wert.length === 0) return null
    return pruefePasswort(wert, umfeld ?? {})
  }, [wert, umfeld])

  const zuKurz = wert.length > 0 && [...wert].length < PASSWORT_MIN
  const meldung = befund && !befund.ok && !zuKurz ? befund.text : null
  const gut = befund?.ok === true

  function vorschlagen(): void {
    const neu = erzeugeStarkesPasswort(zufall)
    aufWert(neu)
    /* Sichtbar machen: Ein Vorschlag, den man nicht lesen kann, ist keiner. */
    setSichtbar(true)
    setKopiert(false)
  }

  async function kopieren(): Promise<void> {
    try {
      await navigator.clipboard.writeText(wert)
      setKopiert(true)
      /* Die Rückmeldung verschwindet wieder – sie ist eine Bestätigung,
         keine Zustandsanzeige. */
      window.setTimeout(() => setKopiert(false), 2500)
    } catch {
      /* Ohne Erlaubnis für die Zwischenablage bleibt das Markieren von Hand.
         Eine Fehlermeldung dafür wäre lauter als das Problem. */
    }
  }

  return (
    <div className={`${k.feld} rp-pwfeld`}>
      <label htmlFor={feldId}>{label}</label>

      <div className={`rp-pwfeld-zeile ${k.zeile}`.trim()}>
        <input
          id={feldId}
          type={sichtbar ? 'text' : 'password'}
          autoComplete="new-password"
          /* Kein `name`: Ein Vorschlag soll nicht in der Formularhistorie
             des Browsers landen, bevor er gespeichert ist. */
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
          value={wert}
          autoFocus={autoFocus}
          disabled={disabled}
          onChange={(e) => {
            aufWert(e.target.value)
            setKopiert(false)
          }}
        />
        <button
          type="button"
          className={`rp-pwfeld-knopf ${k.knopf}`}
          onClick={() => setSichtbar((s) => !s)}
          aria-pressed={sichtbar}
          disabled={disabled}
        >
          {sichtbar ? 'Verbergen' : 'Zeigen'}
        </button>
      </div>

      {mitVorschlag && (
        <div className="rp-pwfeld-werkzeuge">
          <button type="button" className={`rp-pwfeld-knopf ${k.knopf}`} onClick={vorschlagen} disabled={disabled}>
            Starkes Passwort vorschlagen
          </button>
          {wert.length > 0 && (
            <button
              type="button"
              className={`rp-pwfeld-knopf ${k.knopf}`}
              onClick={() => void kopieren()}
              disabled={disabled}
            >
              {kopiert ? 'Kopiert' : 'Kopieren'}
            </button>
          )}
        </div>
      )}

      {meldung ? (
        <span className={k.fehler}>{meldung}</span>
      ) : zuKurz ? (
        <span className={k.hinweis}>Noch {PASSWORT_MIN - [...wert].length} Zeichen.</span>
      ) : gut ? (
        <span className={k.hinweis}>Das genügt. Schreib es dir auf oder speichere es im Passwortverwalter.</span>
      ) : (
        <span className={k.hinweis}>{PASSWORT_HINWEIS}</span>
      )}
    </div>
  )
}
